import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { getStaffMonthlyDetails } from "../../api/attendanceReportApi";
import "./Attendance.css";

const teacherSectionApi = createLookupApi("teacher-sections");
const shiftApi = createLookupApi("shifts");
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const daysInMonth = (year, monthName) => {
  const m = MONTHS.indexOf(monthName);
  return new Date(year, m + 1, 0).getDate();
};

/**
 * Direct port of digicampusbd's `staff-attendance-report-monthly-details`
 * ("Monthly Attendance Details") — per-staff day-by-day pivot for one month,
 * scoped by teacher-section + shift (the old app's own mandatory filters,
 * via the `shift_teacher_assign` join table — added as a new
 * `ShiftTeacherAssign` entity/repository since it wasn't migrated yet).
 * Confirmed via `monthlyStaffAttendanceDetails` this merges real
 * `StaffAttendance` with each staff's own configured weekdays (any weekday
 * NOT in their `StaffAttendanceTimeConfiguration` is their day off) plus
 * institute-wide `OthersHoliday` — new `GET
 * /api/attendance/reports/staff/monthly-details`. `showTimes` toggles
 * between "P" and an "in-out" time-range cell for present days, matching
 * the old app's own `timeShow` option.
 */
export default function StaffMonthlyDetailsPage() {
  const [teacherSections, setTeacherSections] = useState([]);
  const [teacherSectionId, setTeacherSectionId] = useState("");
  const [shifts, setShifts] = useState([]);
  const [shiftId, setShiftId] = useState("");
  const [monthName, setMonthName] = useState(MONTHS[new Date().getMonth()]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showTimes, setShowTimes] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    teacherSectionApi.list().then((list) => {
      setTeacherSections(list);
      if (list.length > 0) setTeacherSectionId(String(list[0].id));
    });
    shiftApi.list().then((list) => {
      setShifts(list);
      if (list.length > 0) setShiftId(String(list[0].id));
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    if (!teacherSectionId || !shiftId) {
      setError("Please select a teacher section and shift.");
      return;
    }
    setLoading(true);
    try {
      setRows(await getStaffMonthlyDetails([teacherSectionId], [shiftId], monthName, year, showTimes));
    } catch (err) {
      setError("Failed to load staff monthly details.");
    } finally {
      setLoading(false);
    }
  };

  const dayCount = daysInMonth(year, monthName);
  const dayCols = Array.from({ length: dayCount }, (_, i) => String(i + 1));

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Staff Attendance — Monthly Details</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          Teacher Section
          <select value={teacherSectionId} onChange={(e) => setTeacherSectionId(e.target.value)}>
            {teacherSections.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Shift
          <select value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
            {shifts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Month
          <select value={monthName} onChange={(e) => setMonthName(e.target.value)}>
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label>
          Year
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </label>
        <label className="checkbox-inline" style={{ display: "inline-flex" }}>
          <input type="checkbox" checked={showTimes} onChange={(e) => setShowTimes(e.target.checked)} />
          Show In/Out Times
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        rows && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Staff ID</th>
                  <th>Staff Name</th>
                  {dayCols.map((d) => (
                    <th key={d}>{d}</th>
                  ))}
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.customId}</td>
                    <td>{r.name}</td>
                    {dayCols.map((d) => (
                      <td key={d}>{r.days[d]}</td>
                    ))}
                    <td>{r.summary}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={dayCols.length + 3}>No staff found for this selection.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
